// Sugalarm — cycle 451.1: the return-to-Now rule, as a real hook.
//
// It lives here rather than inline in `App.tsx` for one reason: a rule that
// decides whether a caregiver can reach Snooze has to be MOUNTABLE in a
// test. Cycle 451's DOM guard had to re-implement `App.tsx`'s chrome
// derivation (mounting the real `App` needs auth, a socket and a router),
// and it said so as a named limitation. This hook is the shipped code path,
// and `alarmRouteReturn.test.ts` mounts THIS.
//
// See `lib/alarmAudible.ts` for the predicate and the reasoning; everything
// this hook knows about alarms arrives through it.

import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  alarmAudioSounding,
  routeReturnExempt,
  shouldReturnToNow,
} from "../lib/alarmAudible";

export interface UseAlarmRouteReturnOpts {
  /** `alarms.audibleAlarmLevel` — the audio bridge's OWN output for this
   *  render, from the one shared `audibleAlarmLevel` derivation. */
  audibleLevel: "low" | "urgent_low" | null;
  /** `alarms.kioskUrgentRepeat` — this surface repeats the urgent-low cue
   *  rather than playing it once. */
  kioskUrgentRepeat: boolean;
  /** `alarms.urgentCueSeq` — bumped each time the off-kiosk urgent-low
   *  one-shot ACTUALLY plays. */
  urgentCueSeq: number;
  /** A Settings "Test alarms" preview is running. */
  previewActive: boolean;
}

/** Take the app to `/now` — where every alarm control mounts — while alarm
 *  audio is sounding, and once when a one-shot cue fires.
 *
 *  ⚠ TWO BEHAVIOURS, BECAUSE THERE ARE TWO KINDS OF ALARM AUDIO.
 *
 *    1. SUSTAINED audio PINS. `low` loops on every surface and the kiosk
 *       repeats its urgent-low cue, so for as long as either is driving, the
 *       app belongs on `/now`. Not edge-triggered: the pathname is a
 *       dependency, so leaving while it still sounds comes straight back.
 *
 *    2. A ONE-SHOT NAVIGATES ONCE AND NEVER PINS. Off the kiosk, cycle 374
 *       made urgent low a ~3 s one-shot, and the LEVEL stays set for the rest
 *       of the episode after the cue has played. Pinning on the level would
 *       hold a phone on `/now` for a whole un-snoozed urgent low on the
 *       strength of three seconds of sound. So the one-shot rides its own
 *       fire edge (`urgentCueSeq`), which the bridge bumps only where the cue
 *       really plays — cycle 381's suppressed cue bumps nothing, and a
 *       preview cannot bump it at all.
 *
 *  ⚠ REPLACE, NOT PUSH. Back must not bounce the caregiver into a loop.
 *
 *  ⚠ NAVIGATION IS THE ONLY SIDE EFFECT. Nothing here writes alarm, custody,
 *  snooze or recovery state, and no alarm surface is added or changed. */
export function useAlarmRouteReturn(
  opts: UseAlarmRouteReturnOpts,
): { alarmSounding: boolean } {
  const location = useLocation();
  const nav = useNavigate();
  const alarmSounding = alarmAudioSounding({
    audibleLevel: opts.audibleLevel,
    kioskUrgentRepeat: opts.kioskUrgentRepeat,
    previewActive: opts.previewActive,
  });

  // 1. Sustained audio pins.
  useEffect(() => {
    if (shouldReturnToNow({ alarmSounding, pathname: location.pathname })) {
      nav("/now", { replace: true });
    }
  }, [alarmSounding, location.pathname, nav]);

  // 2. A one-shot cue navigates once, on its fire edge only.
  const lastCueRef = useRef(opts.urgentCueSeq);
  useEffect(() => {
    if (opts.urgentCueSeq === lastCueRef.current) return;
    lastCueRef.current = opts.urgentCueSeq;
    // ⚠ NO PREVIEW TERM HERE, DELIBERATELY. A preview CANNOT bump this
    // edge: `App.tsx`'s preview effect calls `alarms.playUrgentLowOnce()`
    // directly, while the bump lives inside the DRIVER's own branch, which
    // only a real episode reaches. An earlier draft checked `previewActive`
    // here anyway — and because the ref is advanced first, a 5 s Settings
    // preview overlapping a real cue would have CONSUMED that edge and
    // swallowed the navigation for good, with no pin behind it to recover.
    // The guard could only ever hurt. Caught in external review.
    if (location.pathname === "/now") return;
    if (routeReturnExempt(location.pathname)) return;
    nav("/now", { replace: true });
    // The pathname is deliberately NOT a dependency: this fires on the cue
    // edge and never re-fires when the caregiver navigates away afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.urgentCueSeq]);

  // Returned so `/now` can explain itself rather than let a control
  // silently no-op: while this is true, the Log affordances say why they
  // are not navigating instead of being bounced straight back by rule 1.
  // ONE derivation still — this is the same value rule 1 pinned on.
  return { alarmSounding };
}
